// Thread events — what is visible in a conversation, and the denormalised
// summary fields the conversation list reads straight off the contact row.

import {
  parseElements,
  parseEvent,
  type CartElement,
  type ImpEvent,
  type MessageElement,
} from '$lib/protocol/types';

import { applyCalendarEvents } from './calendar';
import { db, type ContactRow, type EventRow, type OutboxRow } from './db';

/**
 * Saves a page of events. Idempotent on the event id, so fetching the same range
 * again duplicates nothing.
 *
 * `isRead` is deliberately preserved on rewrite: with a blanket replace, every
 * resync reset the flag and the unread counter cleared itself each time the user
 * refreshed — a bug that looks like the app losing track of what was new.
 *
 * The same transaction maintains the contact's denormalised summary (unread
 * count, preview, last-event time) — the IndexedDB replacement for the SQL
 * summary query of the reference client.
 */
export async function saveEvents(accountId: string, events: ImpEvent[]): Promise<void> {
  if (events.length === 0) {
    return;
  }
  await db.transaction('rw', [db.events, db.contacts, db.calendarEntries], async () => {
    let newUnread = 0;
    for (const event of events) {
      const existing = await db.events.get(event.id);
      const row: EventRow = {
        id: event.id,
        accountId,
        sequence: event.sequence,
        timeMs: event.time.getTime(),
        authorKind: event.authorKind,
        authorName: event.authorName ?? null,
        clientUuid: event.clientUuid ?? null,
        elementsJson: JSON.stringify(event.rawElements ?? []),
        isRead: existing?.isRead ?? false,
      };
      await db.events.put(row);
      if (existing === undefined && event.authorKind !== 'buyer') {
        newUnread++;
      }
    }

    await applyCalendarEvents(accountId, events);

    const contact = await db.contacts.get(accountId);
    if (contact !== undefined) {
      const newest = events.reduce((a, b) => (a.sequence >= b.sequence ? a : b));
      const patch: Partial<ContactRow> = {};
      if (newUnread > 0) {
        patch.unreadCount = contact.unreadCount + newUnread;
      }
      if (newest.sequence >= contact.lastEventSequence) {
        patch.lastEventSequence = newest.sequence;
        patch.lastEventTimeMs = newest.time.getTime();
        patch.lastEventJson = JSON.stringify(newest.rawElements ?? []);
        patch.lastFromUser = newest.authorKind === 'buyer';
      }
      if (Object.keys(patch).length > 0) {
        await db.contacts.update(accountId, patch);
      }
    }
  });
}

/** Marks everything the other side sent as read. Called when the conversation is
 *  opened — the only signal available that does not depend on attention. */
export async function markRead(accountId: string): Promise<void> {
  await db.transaction('rw', [db.events, db.contacts], async () => {
    await db.events
      .where('[accountId+sequence]')
      .between([accountId, -Infinity], [accountId, Infinity])
      .filter((e) => !e.isRead && e.authorKind !== 'buyer')
      .modify({ isRead: true });
    await db.contacts.update(accountId, { unreadCount: 0 });
  });
}

export function eventRowsFor(accountId: string): Promise<EventRow[]> {
  return db.events
    .where('[accountId+sequence]')
    .between([accountId, -Infinity], [accountId, Infinity])
    .sortBy('sequence');
}

export function outboxRowsFor(accountId: string): Promise<OutboxRow[]> {
  return db.outbox.where('accountId').equals(accountId).sortBy('createdAtMs');
}

/** An item in a conversation: either an event confirmed by the server, or a
 *  message still waiting in the outbox. The user sees both in the same place —
 *  from their point of view "I wrote it" and "it arrived" are the same moment. */
export interface ConversationItem {
  id: string;
  timeMs: number;
  elements: MessageElement[];
  fromUser: boolean;
  authorKind: string;
  authorName: string | undefined;
  /** undefined for confirmed events; 'queued' or 'error' for outbox items. */
  sendState: 'queued' | 'error' | undefined;
  sendError: string | undefined;
}

export function mergeConversation(events: EventRow[], waiting: OutboxRow[]): ConversationItem[] {
  // An event confirmed by the server carries client_uuid, so the queued item can
  // be recognised and not shown twice — once as pending and once as sent.
  const confirmed = new Set(events.map((e) => e.clientUuid).filter((u): u is string => u !== null));
  const items: ConversationItem[] = [];
  for (const event of events) {
    items.push({
      id: event.id,
      timeMs: event.timeMs,
      elements: parseStoredElements(event.elementsJson),
      fromUser: event.authorKind === 'buyer',
      authorKind: event.authorKind,
      authorName: event.authorName ?? undefined,
      sendState: undefined,
      sendError: undefined,
    });
  }
  for (const item of waiting) {
    if (confirmed.has(item.uuid)) {
      continue;
    }
    items.push({
      id: item.uuid,
      timeMs: item.createdAtMs,
      elements: elementsFromOutboxPayload(item),
      fromUser: true,
      authorKind: 'buyer',
      authorName: undefined,
      sendState: item.permanentlyFailed ? 'error' : 'queued',
      sendError: item.lastErrorCode ?? undefined,
    });
  }
  items.sort((a, b) => a.timeMs - b.timeMs);
  return items;
}

export function parseStoredElements(json: string): MessageElement[] {
  try {
    return parseElements(JSON.parse(json));
  } catch {
    return [];
  }
}

function elementsFromOutboxPayload(item: OutboxRow): MessageElement[] {
  try {
    const payload: unknown = JSON.parse(item.payloadJson);
    if (typeof payload !== 'object' || payload === null) {
      return [];
    }
    if (item.kind === 'message') {
      return parseElements((payload as Record<string, unknown>)['elements']);
    }
    // A queued cart response has to appear as pending too: tapping "I'll order"
    // with no signal must leave a visible trace in the conversation.
    const cartId = (payload as Record<string, unknown>)['cart_id'];
    const decision = (payload as Record<string, unknown>)['decision'];
    return [
      {
        type: 'answer',
        questionId: typeof cartId === 'string' ? cartId : '',
        text: typeof decision === 'string' ? decision : '',
      },
    ];
  } catch {
    return [];
  }
}

/** Orders extracted from the event stream — the latest state per order id wins. */
export async function ordersFor(accountId: string): Promise<MessageElement[]> {
  const rows = await eventRowsFor(accountId);
  const latest = new Map<string, MessageElement>();
  for (const row of rows) {
    for (const element of parseStoredElements(row.elementsJson)) {
      if (element.type === 'order') {
        latest.set(element.orderId, element);
      }
    }
  }
  return [...latest.values()];
}

/** For each cart id, the newest version the CONVERSATION carries. The conversation is
 *  the live record; a cart drawn on a company page is a snapshot of one, so a page needs
 *  this to know whether its copy is still the current one. */
export async function latestCartsFor(accountId: string): Promise<Map<string, CartElement>> {
  const rows = await eventRowsFor(accountId);
  const latest = new Map<string, CartElement>();
  for (const row of rows) {
    for (const element of parseStoredElements(row.elementsJson)) {
      if (element.type === 'cart') {
        latest.set(element.cartId, element);
      }
    }
  }
  return latest;
}

/** All events as parsed models — the export path and the orders screen use this. */
export async function allEvents(accountId: string): Promise<ImpEvent[]> {
  const rows = await eventRowsFor(accountId);
  return rows.map((row) =>
    parseEvent({
      id: row.id,
      thread_id: '',
      sequence: row.sequence,
      time: new Date(row.timeMs).toISOString(),
      author: { kind: row.authorKind, name: row.authorName ?? undefined },
      client_uuid: row.clientUuid ?? undefined,
      elements: JSON.parse(row.elementsJson) as unknown,
    }),
  );
}
